/**
 * The inline recurrence control (TMMAPP-0103, P:2297-2452): a Recurring `aria-pressed` toggle with a
 * repeat icon; when on, "Repeat every" [1 / 2 / 3 / 4 / 6 / 12] [day / week / month / year], 30px
 * weekday circles (weekly), "On day N" / "On the {nth} {weekday}" (monthly and yearly) and the
 * ending (never / on a date / after N) beside a trash that removes the recurrence. It edits the
 * product's one `RecurrenceRule`, through the same transitions the recurrence form uses
 * (`ruleWithFrequency`, `ruleWithEnding`, `ruleWithDayForm`), so the same inputs make the same
 * rule. Meetings reuse it here; the virtual account's meetings tab and the planned pages reuse it
 * next. The summary line is `describeRecurrence`.
 */
import { useId } from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  ToggleButton,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Frequency, RecurrenceRule, Weekday } from '@/api/planned';
import { DeleteIcon, RecurringIcon } from '@/components/icons';
import { denseFieldSx } from '@/components/denseField';
import { useLocale } from '@/i18n/useLocale';
import { layerOverSurface, resolveTokens } from '@/theme/theme';
import {
  describeRecurrence,
  emptyRule,
  endingOf,
  formOf,
  localeWeekdayOrder,
  monthName,
  ordinalOfCivil,
  recurringDefault,
  ruleWithDayForm,
  ruleWithEnding,
  ruleWithFrequency,
  validateRule,
  weekdayName,
  weekdayOfCivil,
  type DayForm,
  type Ending,
} from './recurrence';

const COUNTS = [1, 2, 3, 4, 6, 12] as const;
const UNITS: readonly { frequency: Frequency; labelKey: string }[] = [
  { frequency: 'DAILY', labelKey: 'recurrence.inline.unitDay' },
  { frequency: 'WEEKLY', labelKey: 'recurrence.inline.unitWeek' },
  { frequency: 'MONTHLY', labelKey: 'recurrence.inline.unitMonth' },
  { frequency: 'YEARLY', labelKey: 'recurrence.inline.unitYear' },
];
/** The control's unit and count for a rule: a quarterly rule reads as every 3 months. */
function unitOf(rule: RecurrenceRule): { frequency: Frequency; count: number } {
  const interval = rule.interval ?? 1;
  if (rule.frequency === 'QUARTERLY') return { frequency: 'MONTHLY', count: interval * 3 };
  return { frequency: rule.frequency, count: interval };
}

interface RecurrenceInlineProps {
  /** The rule; `ONCE` is "not recurring". */
  rule: RecurrenceRule;
  onChange: (rule: RecurrenceRule) => void;
  /** The start date (civil), which a monthly "On day N" and the weekday circles default from. */
  startOn: string;
  disabled?: boolean;
}

export function RecurrenceInline({
  rule,
  onChange,
  startOn,
  disabled = false,
}: RecurrenceInlineProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const ids = useId();
  const recurring = rule.frequency !== 'ONCE';
  const { frequency, count } = unitOf(rule);
  const ending = endingOf(rule);
  const dayForm = formOf(rule);
  const error = recurring ? validateRule(rule) : null;
  const weekdays = localeWeekdayOrder(locale);
  const dense = denseFieldSx(tokens);
  const countOptions = COUNTS.includes(count as (typeof COUNTS)[number])
    ? [...COUNTS]
    : [...COUNTS, count].sort((a, b) => a - b);
  const dayNumber = rule.byMonthDays?.[0] ?? Number(startOn.slice(8, 10));

  function withStart(next: RecurrenceRule): RecurrenceRule {
    return { ...next, startOn };
  }

  function toggle() {
    onChange(recurring ? emptyRule(startOn) : recurringDefault(startOn));
  }

  function changeUnit(next: Frequency) {
    const base = rule.frequency === 'QUARTERLY' ? { ...rule, interval: count } : rule;
    const changed = ruleWithFrequency(withStart(base), next);
    onChange(
      next === 'WEEKLY' && (changed.byWeekdays?.length ?? 0) === 0
        ? { ...changed, byWeekdays: [weekdayOfCivil(startOn)] }
        : changed,
    );
  }

  function changeCount(next: number) {
    const base =
      rule.frequency === 'QUARTERLY' ? ruleWithFrequency(withStart(rule), 'MONTHLY') : rule;
    onChange({ ...base, interval: next });
  }

  function toggleWeekday(day: Weekday) {
    const current = rule.byWeekdays ?? [];
    onChange({
      ...rule,
      byWeekdays: current.includes(day) ? current.filter((d) => d !== day) : [...current, day],
    });
  }

  function chooseDayForm(next: DayForm) {
    const changed = ruleWithDayForm(withStart(rule), next);
    onChange(
      next === 'ordinal'
        ? {
            ...changed,
            byWeekOrdinal: ordinalOfCivil(startOn),
            byOrdinalWeekday: weekdayOfCivil(startOn),
          }
        : { ...changed, byMonthDays: [Number(startOn.slice(8, 10))] },
    );
  }

  const monthly = frequency === 'MONTHLY' || frequency === 'YEARLY';
  const monthLabel = monthName(rule.byMonth ?? Number(startOn.slice(5, 7)), locale);

  return (
    <Stack sx={{ gap: 1.5 }}>
      <Box>
        <ToggleButton
          value="recurring"
          selected={recurring}
          onChange={toggle}
          disabled={disabled}
          aria-pressed={recurring}
          size="small"
          sx={{
            gap: 0.75,
            borderRadius: '11px',
            padding: '4px 12px',
            fontSize: 12,
            borderColor: tokens.line,
          }}
        >
          <RecurringIcon />
          {t('recurrence.inline.recurring')}
        </ToggleButton>
      </Box>

      {recurring && (
        <Stack sx={{ gap: 1.5 }} data-recurrence-inline="">
          <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography component="span" sx={{ fontSize: 13, color: tokens.text2 }}>
              {t('recurrence.inline.repeatEvery')}
            </Typography>
            <FormControl sx={{ minWidth: 76, ...dense }}>
              <Select
                value={String(count)}
                disabled={disabled}
                onChange={(event) => changeCount(Number(event.target.value))}
                inputProps={{ 'aria-label': t('recurrence.inline.count') }}
              >
                {countOptions.map((value) => (
                  <MenuItem key={value} value={String(value)}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 120, ...dense }}>
              <Select
                value={frequency}
                disabled={disabled}
                onChange={(event) => changeUnit(event.target.value as Frequency)}
                inputProps={{ 'aria-label': t('recurrence.inline.unit') }}
              >
                {UNITS.map((unit) => (
                  <MenuItem key={unit.frequency} value={unit.frequency}>
                    {t(unit.labelKey)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {frequency === 'WEEKLY' && (
            <Stack
              direction="row"
              role="group"
              aria-label={t('recurrence.weekdays')}
              sx={{ gap: 0.75, flexWrap: 'wrap' }}
            >
              {weekdays.map((day) => {
                const on = (rule.byWeekdays ?? []).includes(day);
                return (
                  <ToggleButton
                    key={day}
                    value={day}
                    selected={on}
                    disabled={disabled}
                    aria-pressed={on}
                    aria-label={weekdayName(day, locale, 'long')}
                    onChange={() => toggleWeekday(day)}
                    sx={{
                      width: 30,
                      height: 30,
                      minWidth: 30,
                      padding: 0,
                      borderRadius: '50%',
                      border: 0,
                      fontSize: 12,
                      fontWeight: 500,
                      background: layerOverSurface(tokens, tokens.hover),
                      color: tokens.text2,
                      '&.Mui-selected, &.Mui-selected:hover': {
                        background: tokens.brand,
                        color: tokens.brandInk,
                      },
                    }}
                  >
                    {weekdayName(day, locale, 'narrow')}
                  </ToggleButton>
                );
              })}
            </Stack>
          )}

          {monthly && (
            <RadioGroup
              value={dayForm}
              onChange={(_event, next) => chooseDayForm(next as DayForm)}
              aria-label={t('recurrence.inline.dayRule')}
            >
              <FormControlLabel
                value="days"
                disabled={disabled}
                control={<Radio size="small" />}
                label={
                  frequency === 'YEARLY'
                    ? t('recurrence.inline.onMonthDay', { month: monthLabel, day: dayNumber })
                    : t('recurrence.inline.onDay', { day: dayNumber })
                }
              />
              <FormControlLabel
                value="ordinal"
                disabled={disabled}
                control={<Radio size="small" />}
                label={t('recurrence.inline.onNth', {
                  nth: t(`recurrence.ordinal.${rule.byWeekOrdinal ?? ordinalOfCivil(startOn)}`),
                  weekday: weekdayName(
                    rule.byOrdinalWeekday ?? weekdayOfCivil(startOn),
                    locale,
                    'long',
                  ),
                })}
              />
            </RadioGroup>
          )}

          <Stack direction="row" sx={{ gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 150, ...dense }}>
              <InputLabel id={`${ids}-ends`}>{t('recurrence.ends')}</InputLabel>
              <Select
                labelId={`${ids}-ends`}
                value={ending}
                disabled={disabled}
                onChange={(event) => onChange(ruleWithEnding(rule, event.target.value as Ending))}
              >
                <MenuItem value="never">{t('recurrence.never')}</MenuItem>
                <MenuItem value="on">{t('recurrence.endsOn')}</MenuItem>
                <MenuItem value="after">{t('recurrence.endsAfter')}</MenuItem>
              </Select>
            </FormControl>
            {ending === 'on' && (
              <TextField
                type="date"
                label={t('recurrence.inline.until')}
                value={rule.endsOn ?? ''}
                disabled={disabled}
                onChange={(event) => onChange({ ...rule, endsOn: event.target.value })}
                sx={dense}
              />
            )}
            {ending === 'after' && (
              <TextField
                type="number"
                label={t('recurrence.endsAfter')}
                value={rule.occurrenceCount ?? 1}
                disabled={disabled}
                onChange={(event) =>
                  onChange({ ...rule, occurrenceCount: Number(event.target.value) })
                }
                slotProps={{ htmlInput: { min: 1, step: 1 } }}
                sx={{ width: 150, ...dense }}
              />
            )}
            <IconButton
              aria-label={t('recurrence.inline.remove')}
              disabled={disabled}
              onClick={() => onChange(emptyRule(startOn))}
              sx={{ mb: '1px' }}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>

          <Typography variant="caption" sx={{ color: tokens.text2 }}>
            {describeRecurrence({ ...rule, startOn }, locale, t)}
          </Typography>
          {error !== null && (
            <FormHelperText error role="alert">
              {t(`recurrence.error.${error}`)}
            </FormHelperText>
          )}
        </Stack>
      )}
    </Stack>
  );
}
